import { supabase } from '../lib/supabase';
import type { Job } from '../modules/dashboard/components/JobCard';
import { cleanSkills } from '../modules/dashboard/components/Jobs/helpers';
import {
  filterBlatantLocationConflicts,
  jobMatchesPreferredCity,
  parseLocationCities,
} from '../modules/dashboard/components/Jobs/jobPreferenceFilter';
import { getTopJobsForUser } from '../modules/recommendations/engine/recommendationEngine';
import type { UserProfile } from '../types/userProfile';
import { getCurrentUserId } from '../utils/authUtils';

const HOME_JOB_SELECT =
  'id, title, company, company_logo_url, location, remote, salary_min, salary_max, salary_currency, compensation_text, description_md, source_channel, skills, must_have_skills, last_seen_at';

const DEFAULT_LOCATION_LABEL = 'India';

const INDIA_LOCATION_TOKENS = [
  'india',
  'bengaluru',
  'bangalore',
  'mumbai',
  'delhi',
  'new delhi',
  'hyderabad',
  'chennai',
  'pune',
  'kolkata',
  'gurugram',
  'gurgaon',
  'noida',
  'ncr',
  'karnataka',
  'maharashtra',
  'telangana',
  'tamil nadu',
  'west bengal',
  'ahmedabad',
  'jaipur',
  'indore',
  'chandigarh',
  'kochi',
  'coimbatore',
  'secunderabad',
];

const NON_INDIA_LOCATION_TOKENS = [
  'united states',
  'united kingdom',
  'canada',
  'australia',
  'san francisco',
  'new york',
  'austin',
  'seattle',
  'mountain view',
  'london',
  'toronto',
  'sydney',
  'singapore',
  'germany',
  'berlin',
  'france',
  'paris',
  'netherlands',
  'amsterdam',
  'california',
  'texas',
];

export type HomepageFeaturedJob = {
  id: string;
  title: string;
  company: string;
  companyLogoUrl?: string;
  location: string;
  salaryLabel: string;
  skills: string[];
  sourceChannel: string;
  matchScore?: number;
};

export type HomepageJobsResult = {
  jobs: HomepageFeaturedJob[];
  locationLabel: string;
};

function norm(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

function formatCurrency(value: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
      notation: value >= 1000 ? 'compact' : 'standard',
    }).format(value);
  } catch {
    return `${currency} ${value.toLocaleString()}`;
  }
}

function extractSalaryFromDescription(md?: string | null): string {
  if (!md?.trim()) return '';

  const text = md
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .slice(0, 4000);

  const patterns: RegExp[] = [
    /(?:₹|Rs\.?|INR)\s*(\d[\d,.]*)\s*(?:-|–|to)\s*(?:₹|Rs\.?|INR)?\s*(\d[\d,.]*)\s*(?:LPA|Lakhs?|L)/i,
    /\$(\d+(?:\.\d+)?)\s*k\s*[-–]\s*\$?(\d+(?:\.\d+)?)\s*k/i,
    /\$(\d{1,3}(?:,\d{3})+)\s*[-–]\s*\$?(\d{1,3}(?:,\d{3})+)/i,
    /(\d{1,2})\s*[-–]\s*(\d{1,2})\s*(?:LPA|Lakhs?|L)\b/i,
    /(?:salary|compensation|ctc|package)\s*[:\-]?\s*([^\n.;]{4,80})/i,
  ];

  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (!match) continue;

    if (match[2] && /\$|k/i.test(match[0])) {
      return `$${match[1]}k–$${match[2]}k`;
    }
    if (match[2] && /₹|Rs|INR|LPA|Lakh/i.test(match[0])) {
      return `₹${match[1]}–${match[2]} LPA`;
    }
    if (match[1] && /salary|compensation|ctc|package/i.test(match[0])) {
      return match[1].trim();
    }
  }

  return '';
}

function formatSalaryLabel(
  min?: number | null,
  max?: number | null,
  currency?: string | null,
  compensationText?: string | null,
  descriptionMd?: string | null,
): string {
  if (compensationText?.trim()) return compensationText.trim();

  const cur = (currency ?? 'INR').trim() || 'INR';
  if (min != null || max != null) {
    if (min != null && max != null) {
      return `${formatCurrency(min, cur)} – ${formatCurrency(max, cur)}`;
    }
    if (min != null) return `From ${formatCurrency(min, cur)}`;
    if (max != null) return `Up to ${formatCurrency(max, cur)}`;
  }

  return extractSalaryFromDescription(descriptionMd);
}

function formatLocation(location?: string | null, remote?: boolean | null): string {
  const loc = (location ?? '').trim();
  if (remote && loc) return `Remote · ${loc}`;
  if (remote) return 'Remote';
  return loc;
}

function formatSource(channel?: string | null): string {
  const raw = (channel ?? 'peerlist').trim().toLowerCase();
  if (raw === 'linkedin') return 'LinkedIn';
  if (raw === 'naukri') return 'Naukri';
  if (raw === 'indeed') return 'Indeed';
  if (raw === 'internshala') return 'Internshala';
  if (raw === 'peerlist') return 'Peerlist';
  return channel?.trim() || 'GoGrabJob';
}

function titleKey(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter((w) => w.length > 2)
    .slice(0, 2)
    .join(' ');
}

function rowHasLocation(row: Record<string, unknown>): boolean {
  return Boolean(row.remote) || Boolean(String(row.location ?? '').trim());
}

function rowHasSalary(row: Record<string, unknown>): boolean {
  return Boolean(
    formatSalaryLabel(
      row.salary_min as number | null,
      row.salary_max as number | null,
      row.salary_currency as string | null,
      row.compensation_text as string | null,
      row.description_md as string | null,
    ),
  );
}

function rowToFilterJob(row: Record<string, unknown>): Job {
  return {
    id: String(row.id ?? ''),
    source: 'direct',
    source_channel: (row.source_channel as string | null) ?? null,
    title: String(row.title ?? ''),
    company: String(row.company ?? ''),
    location: String(row.location ?? ''),
    url: '',
    created: String(row.last_seen_at ?? ''),
    remote: Boolean(row.remote),
    salary_currency: (row.salary_currency as string | null) ?? undefined,
    compensation_text: (row.compensation_text as string | null) ?? undefined,
  };
}

function isClearlyNonIndiaLocation(location: string): boolean {
  const loc = norm(location);
  if (!loc) return false;
  if (loc.includes('india')) return false;

  if (NON_INDIA_LOCATION_TOKENS.some((token) => loc.includes(token))) {
    return true;
  }

  if (/\b(usa|u\.s\.|u\.k\.)\b/.test(loc)) {
    return true;
  }

  // "Mountain View, CA" / "Austin, TX"
  if (/,\s*[a-z]{2}\b/.test(loc)) {
    return true;
  }

  return false;
}

function isIndiaRelevantRow(row: Record<string, unknown>): boolean {
  const channel = norm(row.source_channel as string | null);
  const currency = norm(row.salary_currency as string | null) || 'inr';
  const location = String(row.location ?? '');
  const loc = norm(location);
  const remote = Boolean(row.remote);
  const compensationText = String(row.compensation_text ?? '');
  const descriptionMd = String(row.description_md ?? '');

  if (channel === 'naukri' || channel === 'internshala') return true;
  if (currency === 'inr') return true;
  if (/₹|inr|lpa|lakh/i.test(compensationText) || /₹|inr|lpa|lakh/i.test(descriptionMd)) {
    return true;
  }

  if (loc && INDIA_LOCATION_TOKENS.some((token) => loc.includes(token))) {
    return true;
  }

  if (remote) {
    if (!loc) return true;
    return !isClearlyNonIndiaLocation(location);
  }

  if (!loc) return false;
  return !isClearlyNonIndiaLocation(location);
}

function locationRankScore(
  row: Record<string, unknown>,
  cities: string[],
  profile: UserProfile | null,
): number {
  const job = rowToFilterJob(row);
  let score = 0;

  if (jobMatchesPreferredCity(job, cities)) score += 12;
  if (norm(job.location).includes('india')) score += 6;
  if (norm(job.source_channel) === 'naukri') score += 4;
  if (norm(job.salary_currency) === 'inr' || !job.salary_currency) score += 2;
  if (job.remote) score += 1;

  if (profile && filterBlatantLocationConflicts([job], profile).length === 0) {
    score -= 100;
  }

  return score;
}

function completenessScore(row: Record<string, unknown>): number {
  let score = 0;
  if (rowHasLocation(row)) score += 3;
  if (rowHasSalary(row)) score += 3;
  if (cleanSkills((row.must_have_skills as string[] | null) ?? null).length > 0) score += 1;
  if (cleanSkills((row.skills as string[] | null) ?? null).length > 0) score += 1;
  return score;
}

/** Prefer variety across sources and job titles (not all Product Manager). */
function pickDiverseRows<T extends { title: string; source_channel?: string | null }>(
  rows: T[],
  limit: number,
): T[] {
  const picked: T[] = [];
  const usedChannels = new Set<string>();
  const usedTitles = new Set<string>();

  for (const row of rows) {
    const channel = (row.source_channel ?? 'peerlist').toLowerCase();
    const title = titleKey(row.title);
    const channelOk = !usedChannels.has(channel) || usedChannels.size >= limit;
    const titleOk = !usedTitles.has(title);

    if (channelOk && titleOk) {
      picked.push(row);
      usedChannels.add(channel);
      usedTitles.add(title);
    }
    if (picked.length >= limit) return picked;
  }

  for (const row of rows) {
    if (picked.includes(row)) continue;
    picked.push(row);
    if (picked.length >= limit) break;
  }

  return picked.slice(0, limit);
}

function mapPeerlistRow(
  row: Record<string, unknown>,
  matchScore?: number,
): HomepageFeaturedJob {
  const skills = cleanSkills((row.must_have_skills as string[] | null) ?? null);
  const fallbackSkills = cleanSkills((row.skills as string[] | null) ?? null);

  return {
    id: String(row.id ?? ''),
    title: String(row.title ?? '').trim(),
    company: String(row.company ?? '').trim(),
    companyLogoUrl: String(row.company_logo_url ?? '').trim() || undefined,
    location: formatLocation(row.location as string | null, row.remote as boolean | null),
    salaryLabel: formatSalaryLabel(
      row.salary_min as number | null,
      row.salary_max as number | null,
      row.salary_currency as string | null,
      row.compensation_text as string | null,
      row.description_md as string | null,
    ),
    skills: (skills.length > 0 ? skills : fallbackSkills).slice(0, 3),
    sourceChannel: formatSource(row.source_channel as string | null),
    matchScore,
  };
}

function isDisplayReady(job: HomepageFeaturedJob): boolean {
  return (
    job.title.length > 0 &&
    job.company.length > 0 &&
    job.location.length > 0 &&
    job.salaryLabel.length > 0
  );
}

type HomepageLocationContext = {
  locationLabel: string;
  cities: string[];
  profile: UserProfile | null;
};

async function resolveHomepageLocationContext(): Promise<HomepageLocationContext> {
  const userId = await getCurrentUserId();
  if (!userId) {
    return {
      locationLabel: DEFAULT_LOCATION_LABEL,
      cities: parseLocationCities(DEFAULT_LOCATION_LABEL),
      profile: null,
    };
  }

  const { data, error } = await supabase
    .from('user_profiles')
    .select('location, preferred_locations')
    .eq('id', userId)
    .maybeSingle();

  if (error) {
    console.warn('[homepageJobs] profile location fetch failed:', error.message);
  }

  const preferredLocation =
    data?.location?.trim() ||
    data?.preferred_locations?.find((entry: string) => entry?.trim())?.trim() ||
    DEFAULT_LOCATION_LABEL;

  const cities = parseLocationCities(preferredLocation);
  const profile: UserProfile = {
    id: userId,
    displayName: null,
    headline: null,
    currentRole: null,
    experienceYears: null,
    skills: [],
    industry: null,
    location: preferredLocation,
    jobSearchStatus: null,
    preferredJobTypes: [],
    salaryExpectationInr: null,
    preferredRoles: [],
    noticePeriod: null,
    holdingOffers: null,
    currentSalary: null,
    salaryCurrency: null,
    companySizePrefs: [],
    genderIdentity: null,
    dateOfBirth: null,
    preferredLocations: data?.preferred_locations ?? [],
    linkedinUrl: null,
    githubUrl: null,
    portfolioUrl: null,
    professionalSummary: null,
    experiences: [],
    education: [],
    projects: [],
    certifications: [],
    languages: [],
    resumeSource: null,
    resumeFileUrl: null,
    resumeFileName: null,
    resumeRawText: null,
    resumeUpdatedAt: null,
    onboardingCompleted: true,
    blindMode: false,
    tourCompleted: false,
    lastReviewPromptAt: null,
    createdAt: '',
    updatedAt: '',
  };

  return {
    locationLabel: preferredLocation,
    cities: cities.length > 0 ? cities : parseLocationCities(DEFAULT_LOCATION_LABEL),
    profile,
  };
}

function filterRowsForLocation(
  rows: Record<string, unknown>[],
  context: HomepageLocationContext,
): Record<string, unknown>[] {
  const filtered = rows.filter((row) => {
    if (!isIndiaRelevantRow(row)) return false;

    const job = rowToFilterJob(row);
    if (context.profile) {
      return filterBlatantLocationConflicts([job], context.profile).length > 0;
    }

    return !isClearlyNonIndiaLocation(String(row.location ?? ''));
  });

  return filtered.sort((a, b) => {
    const locationDelta =
      locationRankScore(b, context.cities, context.profile) -
      locationRankScore(a, context.cities, context.profile);
    if (locationDelta !== 0) return locationDelta;
    return completenessScore(b) - completenessScore(a);
  });
}

async function fetchPersonalizedJobs(
  userId: string,
  limit: number,
  context: HomepageLocationContext,
): Promise<HomepageFeaturedJob[]> {
  const recs = await getTopJobsForUser(userId, limit * 3);
  if (!recs.length) return [];

  const scoreById = new Map(recs.map((r) => [String(r.job_id), Math.round(Number(r.score))]));
  const ids = [...scoreById.keys()];

  const { data, error } = await supabase
    .from('peerlist_jobs')
    .select(HOME_JOB_SELECT)
    .eq('is_active', true)
    .in('id', ids);

  if (error || !data?.length) return [];

  const byId = new Map(
    (data as Record<string, unknown>[]).map((row) => [String(row.id), row]),
  );

  const orderedRows = ids
    .map((id) => byId.get(id))
    .filter((row): row is Record<string, unknown> => row != null);

  const locationFiltered = filterRowsForLocation(orderedRows, context);

  return locationFiltered
    .map((row) => mapPeerlistRow(row, scoreById.get(String(row.id))))
    .filter(isDisplayReady)
    .slice(0, limit);
}

const INDIA_DB_OR_FILTER = [
  'location.ilike.%India%',
  'location.ilike.%Bengaluru%',
  'location.ilike.%Bangalore%',
  'location.ilike.%Mumbai%',
  'location.ilike.%Delhi%',
  'location.ilike.%Hyderabad%',
  'location.ilike.%Chennai%',
  'location.ilike.%Pune%',
  'location.ilike.%Kolkata%',
  'location.ilike.%Gurugram%',
  'location.ilike.%Gurgaon%',
  'location.ilike.%Noida%',
  'source_channel.eq.naukri',
  'salary_currency.eq.INR',
].join(',');

async function fetchCatalogJobs(
  limit: number,
  context: HomepageLocationContext,
): Promise<HomepageFeaturedJob[]> {
  const { data, error } = await supabase
    .from('peerlist_jobs')
    .select(HOME_JOB_SELECT)
    .eq('is_active', true)
    .or(INDIA_DB_OR_FILTER)
    .order('last_seen_at', { ascending: false })
    .limit(160);

  if (error || !data?.length) {
    if (error) console.warn('[homepageJobs] catalog fetch failed:', error.message);
    return [];
  }

  const rows = filterRowsForLocation(data as Record<string, unknown>[], context)
    .filter((row) => rowHasLocation(row) && rowHasSalary(row));

  const diverse = pickDiverseRows(rows, limit);

  return diverse
    .map((row) => mapPeerlistRow(row))
    .filter(isDisplayReady);
}

/** Live jobs for the marketing homepage — personalized when signed in. */
export async function fetchHomepageFeaturedJobs(limit = 12): Promise<HomepageJobsResult> {
  const context = await resolveHomepageLocationContext();

  try {
    const userId = await getCurrentUserId();
    if (userId) {
      const personal = await fetchPersonalizedJobs(userId, limit, context);
      if (personal.length >= Math.min(limit, 3)) {
        return { jobs: personal, locationLabel: context.locationLabel };
      }
    }
  } catch (err) {
    console.warn('[homepageJobs] personalized fetch failed:', err);
  }

  const jobs = await fetchCatalogJobs(limit, context);
  return { jobs, locationLabel: context.locationLabel };
}
